// src/components/tests/UploadModal.jsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  LinearProgress,
  Typography,
  Box,
  Stack,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, CloudUpload as CloudUploadIcon, Close as CloseIcon, CameraAlt as CameraIcon, FlipCameraIos as FlipCameraIcon, PhotoCamera as PhotoCameraIcon, Edit as EditIcon } from '@mui/icons-material';
import uploadService from '../../services/uploadService';
import PdfAnnotationViewer from './PdfAnnotationViewer';
import ResumableWorksheetUpload from '../worksheet/ResumableWorksheetUpload';
import {
  MAX_FILE_SIZE_MB,
  MAX_TOTAL_UPLOAD_SIZE_MB,
  ALLOWED_FILE_TYPES,
} from '../../config/Config';

const bytesInMB = (mb) => mb * 1024 * 1024;

const FACING_MODES = { user: 'user', environment: 'environment' };

const UploadModal = ({
  open,
  onClose,
  studentId,
  teacherManagedStudentId,
  testId,
  role = 'student',
  readOnly = false,
  title,
  canAnnotate = false,
  allowTeacherUpload = false,
  showTeacherUploads = false,
  attemptId,
  questions,
  revisionData,
  currentUserId = null,
  /** When true with roster (teacherManagedStudentId), show the same ResumableWorksheetUpload UI as students; files save as teacher uploads for that roster row. */
  studentWorksheetUiForManagedRoster = false,
}) => {
  const modalTitle = title || (readOnly ? 'Uploaded Documents' : 'Worksheet and Instruction');
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraFacing, setCameraFacing] = useState(FACING_MODES.environment);
  const [cameraError, setCameraError] = useState('');
  const [annotateUpload, setAnnotateUpload] = useState(null);
  const [teacherUploads, setTeacherUploads] = useState([]);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const listRole = allowTeacherUpload ? 'student' : role;
  const shouldFetchTeacherUploads = allowTeacherUpload || showTeacherUploads;

  // Use resumable worksheet flow for student worksheet upload (one PDF via scan or file)
  const rosterOnly = teacherManagedStudentId != null && teacherManagedStudentId !== '';
  const useResumableWorksheet =
    !readOnly &&
    !allowTeacherUpload &&
    !!testId &&
    ((role === 'student' && !!studentId && !rosterOnly) ||
      (rosterOnly && !!teacherManagedStudentId && studentWorksheetUiForManagedRoster));
  const [showResumableFlow, setShowResumableFlow] = useState(true);
  const [completedWorksheetUpload, setCompletedWorksheetUpload] = useState(null);
  const [openingPdfId, setOpeningPdfId] = useState(null);

  // Fetch current uploads
  const fetchUploads = async () => {
    try {
      setLoading(true);
      if (rosterOnly) {
        const teacherData = await uploadService.listUploads({
          teacherManagedStudentId,
          testId,
          role: 'teacher',
        });
        setUploads([]);
        setTeacherUploads(Array.isArray(teacherData) ? teacherData : []);
      } else {
        const data = await uploadService.listUploads({ studentId, testId, role: listRole });
        setUploads(Array.isArray(data) ? data : []);
        if (shouldFetchTeacherUploads) {
          const teacherData = await uploadService.listUploads({ studentId, testId, role: 'teacher' });
          setTeacherUploads(Array.isArray(teacherData) ? teacherData : []);
        } else {
          setTeacherUploads([]);
        }
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOpen(false);
    setCameraError('');
  };

  const handleClose = () => {
    stopCamera();
    setShowResumableFlow(true);
    setCompletedWorksheetUpload(null);
    onClose();
  };

  const handleResumableComplete = async (finalPdfUrl) => {
    if (!finalPdfUrl || !testId) {
      handleClose();
      return;
    }
    if (rosterOnly && teacherManagedStudentId && studentWorksheetUiForManagedRoster) {
      try {
        const res = await fetch(finalPdfUrl, { credentials: 'include' });
        const blob = await res.blob();
        const file = new File([blob], 'worksheet.pdf', { type: 'application/pdf' });
        const existing = await uploadService.listUploads({
          teacherManagedStudentId,
          testId,
          role: 'teacher',
        });
        const list = Array.isArray(existing) ? existing : [];
        for (const u of list) {
          try {
            await uploadService.deleteUpload(u.id, 'teacher');
          } catch (_) {}
        }
        await uploadService.uploadFile({
          teacherManagedStudentId,
          testId,
          file,
          role: 'teacher',
        });
        await fetchUploads();
        const currentList = await uploadService.listUploads({
          teacherManagedStudentId,
          testId,
          role: 'teacher',
        });
        const arr = Array.isArray(currentList) ? currentList : [];
        const added = arr.find((u) => (u.filename || '').toLowerCase().endsWith('.pdf')) || arr[0];
        setCompletedWorksheetUpload(added || null);
      } catch (err) {
        console.warn('Could not register worksheet PDF with uploads:', err);
      }
      return;
    }
    if (!studentId) {
      handleClose();
      return;
    }
    try {
      const res = await fetch(finalPdfUrl, { credentials: 'include' });
      const blob = await res.blob();
      const file = new File([blob], 'worksheet.pdf', { type: 'application/pdf' });
      const existing = await uploadService.listUploads({ studentId, testId, role: 'student' });
      const list = Array.isArray(existing) ? existing : [];
      for (const u of list) {
        try {
          await uploadService.deleteUpload(u.id, 'student');
        } catch (_) {}
      }
      await uploadService.uploadFile({ studentId, testId, file, role: 'student' });
      await fetchUploads();
      const currentList = await uploadService.listUploads({ studentId, testId, role: 'student' });
      const arr = Array.isArray(currentList) ? currentList : [];
      const added = arr.find((u) => (u.filename || '').toLowerCase().endsWith('.pdf')) || arr[0];
      setCompletedWorksheetUpload(added || null);
    } catch (err) {
      console.warn('Could not register worksheet PDF with uploads:', err);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUploads();
      setFiles([]);
      setUploadProgress({});
      setErrorMsg('');
      setShowResumableFlow(true);
      setCompletedWorksheetUpload(null);
    } else {
      stopCamera();
    }
  }, [open]);

  useEffect(() => {
    const hasAnnotated = uploads.some((u) => u.hasTeacherAnnotations ?? u.has_teacher_annotations);
    if (hasAnnotated && cameraOpen) stopCamera();
  }, [uploads]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const startCamera = async (facing = cameraFacing) => {
    setCameraError('');
    stopCamera();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraFacing(facing);
      setCameraOpen(true);
    } catch (err) {
      setCameraError(err.message || 'Could not access camera. Please allow camera permissions.');
    }
  };

  useEffect(() => {
    if (cameraOpen && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraOpen]);

  const switchCamera = async () => {
    const next = cameraFacing === FACING_MODES.user ? FACING_MODES.environment : FACING_MODES.user;
    await startCamera(next);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !streamRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const filename = `camera-${Date.now()}.jpg`;
        const file = new File([blob], filename, { type: 'image/jpeg' });
        if (file.size > bytesInMB(MAX_FILE_SIZE_MB)) {
          setErrorMsg(`Captured image exceeds ${MAX_FILE_SIZE_MB} MB limit.`);
          return;
        }
        const totalSize = files.reduce((acc, f) => acc + f.size, 0) + file.size;
        if (totalSize > bytesInMB(MAX_TOTAL_UPLOAD_SIZE_MB)) {
          setErrorMsg(`Upload exceeds ${MAX_TOTAL_UPLOAD_SIZE_MB} MB aggregate limit.`);
          return;
        }
        setFiles((prev) => (uploadRole === 'student' ? [file] : [...prev, file]));
        setErrorMsg('');
      },
      'image/jpeg',
      0.92
    );
  };

  // Handle selection of files
  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const allowed = selected.filter((file) => {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      return ALLOWED_FILE_TYPES.includes(ext);
    });
    const invalid = selected.length - allowed.length;
    if (invalid) {
      setErrorMsg(`Ignored ${invalid} unsupported file(s). Allowed: ${ALLOWED_FILE_TYPES.join(', ')}`);
    }

    // Enforce per file size
    const tooLarge = allowed.filter((f) => f.size > bytesInMB(MAX_FILE_SIZE_MB));
    if (tooLarge.length) {
      setErrorMsg(`Some files exceed the ${MAX_FILE_SIZE_MB} MB limit and were skipped.`);
    }

    let valid = allowed.filter((f) => f.size <= bytesInMB(MAX_FILE_SIZE_MB));

    // Students: only one document - replace with new selection
    const isStudentFlow = !allowTeacherUpload;
    if (isStudentFlow && valid.length > 0) {
      valid = [valid[0]];
    }

    // Check total size
    const totalSize = valid.reduce((acc, f) => acc + f.size, 0);
    if (totalSize > bytesInMB(MAX_TOTAL_UPLOAD_SIZE_MB)) {
      setErrorMsg(`Upload exceeds ${MAX_TOTAL_UPLOAD_SIZE_MB} MB aggregate limit.`);
      return;
    }
    setFiles(valid);
    e.target.value = '';
  };

  const uploadRole = allowTeacherUpload ? 'teacher' : role;
  const isStudentUpload = uploadRole === 'student';
  const hasAnyTeacherAnnotations = uploads.some((u) => u.hasTeacherAnnotations ?? u.has_teacher_annotations);
  const uploadDisabled = hasAnyTeacherAnnotations;

  // Execute upload
  const handleUpload = async () => {
    setUploading(true);
    if (isStudentUpload && uploads.length > 0) {
      for (const u of uploads) {
        try {
          await uploadService.deleteUpload(u.id, listRole);
        } catch (err) {
          const msg = err?.response?.data?.message || err?.message || '';
          if (!msg.includes('BucketNotFound') && !msg.includes('bucket')) {
            console.warn('Delete before replace:', err);
          }
        }
      }
    }
    for (const file of files) {
      try {
        await uploadService.uploadFile({
          studentId,
          teacherManagedStudentId: rosterOnly ? teacherManagedStudentId : undefined,
          testId,
          file,
          role: uploadRole,
          onUploadProgress: (e) => {
            const percent = Math.round((e.loaded * 100) / e.total);
            setUploadProgress((prev) => ({ ...prev, [file.name]: percent }));
          },
        });
      } catch (err) {
        setErrorMsg(`Failed to upload ${file.name}`);
      }
    }
    setFiles([]);
    await fetchUploads();
    setUploading(false);
  };

  const handleDeleteTeacherUpload = async (uploadId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await uploadService.deleteUpload(uploadId, 'teacher');
      fetchUploads();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.message || '';
      if (!errorMsg.includes('BucketNotFound') && !errorMsg.includes('bucket')) {
        console.warn('Delete warning:', errorMsg);
      }
      fetchUploads();
    }
  };

  const handleDelete = async (uploadId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await uploadService.deleteUpload(uploadId, role);
      // Refresh list - DB record is deleted even if OCI delete had issues
      fetchUploads();
    } catch (err) {
      // Backend returns 204 even if OCI delete fails, so this shouldn't happen
      // But if it does, refresh anyway to sync state
      const errorMsg = err?.response?.data?.message || err?.message || '';
      // Ignore OCI bucket errors - they're non-critical, DB record is deleted
      if (!errorMsg.includes('BucketNotFound') && !errorMsg.includes('bucket')) {
        console.warn('Delete warning:', errorMsg);
      }
      fetchUploads();
    }
  };

  const resumableExistingPdf =
    rosterOnly && studentWorksheetUiForManagedRoster
      ? teacherUploads.find((u) => (u.filename || '').toLowerCase().endsWith('.pdf')) || teacherUploads[0] || null
      : uploads.find((u) => (u.filename || '').toLowerCase().endsWith('.pdf')) || uploads[0] || null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {modalTitle}
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {useResumableWorksheet && showResumableFlow ? (
          <ResumableWorksheetUpload
            testId={testId ?? undefined}
            testAttemptId={attemptId ?? undefined}
            onComplete={handleResumableComplete}
            onCancel={handleClose}
            completedUpload={completedWorksheetUpload}
            existingUpload={resumableExistingPdf}
            worksheetUploadApiRole={
              rosterOnly && studentWorksheetUiForManagedRoster ? 'teacher' : 'student'
            }
          />
        ) : (
        <>
        {allowTeacherUpload && !uploadDisabled && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Upload
              <input hidden multiple type="file" accept={ALLOWED_FILE_TYPES.join(',')} onChange={handleFileChange} />
            </Button>
          </Box>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <>
            {!rosterOnly && uploads.length === 0 && !(shouldFetchTeacherUploads && teacherUploads.length > 0) && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                No files uploaded yet.
              </Typography>
            )}
            {rosterOnly && teacherUploads.length === 0 && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                No files uploaded yet.
              </Typography>
            )}

            {!rosterOnly && uploads.length > 0 && (
              <List dense>
                {uploads.map((u) => {
                  const isPdf = (u.filename || '').toLowerCase().endsWith('.pdf');
                  const useAuthOpen = isPdf && u.id;
                  const linkHref = useAuthOpen ? undefined : u.url;
                  const isOpening = openingPdfId === u.id;
                  const handleRowClick = useAuthOpen
                    ? async (e) => {
                        e.preventDefault();
                        setOpeningPdfId(u.id);
                        try {
                          await uploadService.openStudentUploadPdf(u.id, u.filename);
                        } finally {
                          setOpeningPdfId(null);
                        }
                      }
                    : undefined;
                  return (
                    <ListItem
                      key={u.id}
                      divider
                      secondaryAction={
                        <>
                          {canAnnotate && isPdf && (
                            <Tooltip title="Review paper">
                              <IconButton
                                edge="end"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setAnnotateUpload(u);
                                }}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!readOnly && (
                            <Tooltip title={(u.hasTeacherAnnotations ?? u.has_teacher_annotations) ? 'Graded by Teacher' : 'Delete'}>
                              <span>
                                <IconButton
                                  edge="end"
                                  disabled={u.hasTeacherAnnotations ?? u.has_teacher_annotations}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!(u.hasTeacherAnnotations ?? u.has_teacher_annotations)) handleDelete(u.id);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </>
                      }
                      button
                      component={useAuthOpen ? 'div' : 'a'}
                      href={linkHref}
                      target={useAuthOpen ? undefined : '_blank'}
                      rel={useAuthOpen ? undefined : 'noopener noreferrer'}
                      onClick={handleRowClick}
                      disabled={useAuthOpen ? isOpening : undefined}
                      sx={useAuthOpen && isOpening ? { opacity: 0.7 } : undefined}
                    >
                      <ListItemText
                        primary={u.filename}
                        secondary={isOpening ? 'Opening…' : `${(u.size / (1024 * 1024)).toFixed(2)} MB · ${new Date(u.uploadedAt).toLocaleString()}`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
            {!rosterOnly && shouldFetchTeacherUploads && teacherUploads.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Teacher uploads</Typography>
                  <List dense>
                    {teacherUploads.map((u) => (
                      <ListItem
                        key={u.id}
                        divider
                        secondaryAction={
                          allowTeacherUpload ? (
                            <IconButton
                              edge="end"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteTeacherUpload(u.id); }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          ) : null
                        }
                        button
                        component="a"
                        href={u.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ListItemText
                          primary={u.filename}
                          secondary={`${(u.size / (1024 * 1024)).toFixed(2)} MB · ${new Date(u.uploadedAt).toLocaleString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
            )}
            {rosterOnly && teacherUploads.length > 0 && (
              <List dense>
                {teacherUploads.map((u) => (
                  <ListItem
                    key={u.id}
                    divider
                    secondaryAction={
                      allowTeacherUpload ? (
                        <IconButton
                          edge="end"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteTeacherUpload(u.id); }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      ) : null
                    }
                    button
                    component="a"
                    href={u.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ListItemText
                      primary={u.filename}
                      secondary={`${(u.size / (1024 * 1024)).toFixed(2)} MB · ${new Date(u.uploadedAt).toLocaleString()}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {!readOnly && !uploadDisabled && (
              <Box mt={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                  >
                    Choose Files
                    <input hidden multiple={!isStudentUpload} type="file" accept={ALLOWED_FILE_TYPES.join(',')} onChange={handleFileChange} />
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CameraIcon />}
                    onClick={() => startCamera()}
                  >
                    Camera
                  </Button>
                </Stack>

                {cameraOpen && (
                  <Box mt={2} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: 'grey.900' }}>
                    <Box sx={{ 
                      position: 'relative', 
                      width: '100%', 
                      maxWidth: 400, 
                      mx: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <Box sx={{
                        position: 'relative',
                        width: '100%',
                        maxHeight: { xs: '40vh', sm: '50vh' },
                        overflow: 'hidden',
                        borderRadius: 4,
                        backgroundColor: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          style={{ 
                            width: '100%', 
                            height: 'auto',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            display: 'block'
                          }}
                        />
                      </Box>
                      <Stack 
                        direction="row" 
                        spacing={1} 
                        justifyContent="center" 
                        mt={1}
                        sx={{ width: '100%', flexWrap: 'wrap', gap: 1 }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FlipCameraIcon />}
                          onClick={switchCamera}
                        >
                          Switch Camera
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PhotoCameraIcon />}
                          onClick={capturePhoto}
                        >
                          Capture
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                )}

                {cameraError && (
                  <Typography color="error" variant="body2" mt={1}>{cameraError}</Typography>
                )}
              </Box>
            )}

            {!readOnly && files.length > 0 && (
              <Box mt={2}>
                {files.map((f) => (
                  <Box key={f.name} mb={1}>
                    <Typography variant="body2">{f.name}</Typography>
                    <LinearProgress variant="determinate" value={uploadProgress[f.name] || 0} />
                  </Box>
                ))}
              </Box>
            )}

            {errorMsg && (
              <Typography color="error" variant="body2" mt={1}>{errorMsg}</Typography>
            )}
          </>
        )}
        </>
        )}
      </DialogContent>
      <PdfAnnotationViewer
        open={!!annotateUpload}
        onClose={() => {
          setAnnotateUpload(null);
          fetchUploads();
        }}
        uploadId={annotateUpload?.id}
        filename={annotateUpload?.filename}
        role={role}
        attemptId={attemptId}
        questions={questions}
        revisionData={revisionData}
        currentUserId={currentUserId}
      />
      {(!useResumableWorksheet || !showResumableFlow) && (
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {(!readOnly || allowTeacherUpload) && !uploadDisabled && !useResumableWorksheet && (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
          >
            Upload
          </Button>
        )}
      </DialogActions>
      )}
    </Dialog>
  );
};

export default UploadModal;
